import {
  type AssetManifest,
  type Facing,
  FIXED_PROP_IDS,
  footprintOf,
  GENERATED_WORLD,
  MOBS,
  type PropPlacement,
  PROPS,
  TILE_SIZE,
  WORLD_EDITS,
} from '@shefira/shared';
import Phaser from 'phaser';

import type { ChestLayer } from '../game/chests.js';
import { drawProp, placeProp, type PropImage } from '../game/props.js';
import { EditModel } from './edit-model.js';
import { EditorOverlay } from './editor-overlay.js';
import { EditorPanel } from './editor-panel.js';
import { Palette, type PaletteEntry } from './palette.js';
import { saveWorldEdits } from './save-edits.js';
import {
  boxOf,
  describe,
  positionOf,
  propOf,
  sameSelection,
  type Selection,
  spawnOf,
} from './selection.js';

/**
 * The in-game world editor (ADR 0022), for admins: F2 opens it over the
 * real world. A click picks a prop, an inhabitant, a portal or a chest; a
 * drag moves it by whole tiles; the panel sets new props down. The world
 * redraws at once, the checks run at every change, and Ctrl+S sends the
 * edits to the server, which checks them again and writes them into the
 * repository. Other players see them once the server restarts on the file.
 */
export interface WorldEditorOptions {
  scene: Phaser.Scene;
  manifest: AssetManifest;
  /** The zone's prop images, in the order of `zone.props`. */
  props: readonly (PropImage | undefined)[];
  chests: ChestLayer | undefined;
  /** The live monster or townsperson drawn over a tile, if any (its sprite is taller than its tile). */
  mobAt: (tile: { x: number; y: number }) => { mob: string; x: number; y: number } | undefined;
  token: string;
  canSave: boolean;
  /** Told when the editor opens or closes: the scene stops following the player meanwhile. */
  onToggle?: (active: boolean) => void;
}

interface Drag {
  selection: Selection;
  grab: { x: number; y: number };
  from: { x: number; y: number };
}

interface Pan {
  pointer: { x: number; y: number };
  scroll: { x: number; y: number };
}

const FACINGS: readonly Facing[] = ['south', 'west', 'north', 'east'];
const PAN_TILES = 6;
/**
 * The editor's zooms, out from the game's: whole fractions, so a pixel of
 * the world is always 1/2, 1/3, 1/4… of what it is in play. Below 1 the
 * world is not pixel-perfect (CLAUDE.md rule 6): an admin's overview, never
 * what players see.
 */
const ZOOMS = [1, 1 / 2, 1 / 3, 1 / 4, 1 / 6, 1 / 8];

export class WorldEditor {
  private readonly model = new EditModel(GENERATED_WORLD, WORLD_EDITS);
  private readonly panel: EditorPanel;
  private readonly overlay: EditorOverlay;
  private readonly palette: Palette;
  private readonly images = new Map<string, { image: PropImage; placed: PropPlacement }>();
  /** The spawns as the server loaded them: where the live inhabitants belong. */
  private readonly served = this.model.world.spawns;
  /** Spawns added by an earlier save, which the server already brought to life. */
  private readonly servedOrigins = new Set(
    this.served.map((spawn) => spawn.origin).filter((origin) => origin.startsWith('added-spawn:')),
  );
  private selection?: Selection;
  private hover?: Selection;
  private drag?: Drag;
  private pan?: Pan;
  private armed?: PaletteEntry;
  /** Inhabitants set down in this session, drawn where they will stand once the server restarts. */
  private readonly ghosts = new Map<string, Phaser.GameObjects.Image>();
  private saving = false;
  private isActive = false;
  private zoomStep = 0;

  constructor(private readonly options: WorldEditorOptions) {
    const { scene } = options;
    // The zone's props are the saved edits applied, in the same order.
    this.model.world.props.forEach((edited, index) => {
      const image = options.props[index];
      if (image !== undefined) this.images.set(edited.origin, { image, placed: edited.placement });
    });
    this.overlay = new EditorOverlay(scene);
    const palette = new Palette({
      scene,
      manifest: options.manifest,
      onPick: (entry) => {
        this.setArmed(entry);
      },
    });
    this.palette = palette;
    this.panel = new EditorPanel({
      canSave: options.canSave,
      palette: palette.root,
      onSpawnSize: (size) => {
        this.resize(size);
      },
      onClose: () => {
        this.toggle(false);
      },
      onSave: () => {
        void this.save();
      },
      onUndo: () => {
        this.undo();
      },
    });
    scene.input.on(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown);
    scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    scene.input.on(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    scene.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    // Capture: the editor's keys must not also reach the game's (P, M, T, W…).
    window.addEventListener('keydown', this.onKeyDown, { capture: true });
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  get active(): boolean {
    return this.isActive;
  }

  toggle(active = !this.isActive): void {
    if (active === this.isActive) return;
    this.isActive = active;
    this.panel.visible = active;
    this.overlay.setVisible(active);
    for (const image of this.ghosts.values()) image.setVisible(active);
    if (active) {
      this.panel.say(
        this.options.canSave
          ? ''
          : 'Production : les retouches ne peuvent pas être enregistrées ici.',
      );
      this.select(this.selection);
      this.refresh();
    } else {
      this.drag = undefined;
      this.pan = undefined;
      this.setArmed(undefined);
      this.zoomTo(0);
      this.options.scene.input.setDefaultCursor('var(--cursor-default)');
    }
    this.options.onToggle?.(active);
  }

  destroy(): void {
    const { scene } = this.options;
    scene.input.off(Phaser.Input.Events.POINTER_DOWN, this.onPointerDown);
    scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.onPointerMove);
    scene.input.off(Phaser.Input.Events.POINTER_UP, this.onPointerUp);
    scene.input.off(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    window.removeEventListener('keydown', this.onKeyDown, { capture: true });
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    this.panel.destroy();
    this.overlay.destroy();
    for (const image of this.ghosts.values()) image.destroy();
  }

  private readonly onPointerDown = (pointer: Phaser.Input.Pointer): void => {
    if (!this.isActive) return;
    const tile = this.tileOf(pointer);
    if (this.armed !== undefined) {
      this.setDown(this.armed, tile);
      return;
    }
    const picked = this.pick(pointer);
    this.select(picked);
    const from = picked === undefined ? undefined : positionOf(this.model.world, picked);
    if (picked !== undefined && from !== undefined) {
      this.model.checkpoint();
      this.drag = { selection: picked, grab: tile, from };
    } else {
      // A drag on bare ground moves the view.
      const camera = this.options.scene.cameras.main;
      this.pan = {
        pointer: { x: pointer.x, y: pointer.y },
        scroll: { x: camera.scrollX, y: camera.scrollY },
      };
    }
    this.refresh();
  };

  private readonly onPointerMove = (pointer: Phaser.Input.Pointer): void => {
    if (!this.isActive) return;
    const { scene } = this.options;
    if (this.pan !== undefined) {
      const camera = scene.cameras.main;
      camera.setScroll(
        Math.round(this.pan.scroll.x - (pointer.x - this.pan.pointer.x) / camera.zoom),
        Math.round(this.pan.scroll.y - (pointer.y - this.pan.pointer.y) / camera.zoom),
      );
      return;
    }
    if (this.drag !== undefined) {
      const tile = this.tileOf(pointer);
      const to = {
        x: this.drag.from.x + tile.x - this.drag.grab.x,
        y: this.drag.from.y + tile.y - this.drag.grab.y,
      };
      const now = positionOf(this.model.world, this.drag.selection);
      if (now !== undefined && (now.x !== to.x || now.y !== to.y)) {
        this.moveTo(this.drag.selection, to);
        this.refresh();
      }
      return;
    }
    const hover = this.pick(pointer);
    if (!sameSelection(hover, this.hover)) {
      this.hover = hover;
      this.drawOutline();
    }
    const cursor =
      this.armed !== undefined ? 'copy' : hover !== undefined ? 'move' : 'var(--cursor-grab)';
    scene.input.setDefaultCursor(cursor);
  };

  private readonly onPointerUp = (): void => {
    this.drag = undefined;
    this.pan = undefined;
  };

  private readonly onWheel = (
    pointer: Phaser.Input.Pointer,
    _over: unknown,
    _dx: number,
    dy: number,
  ): void => {
    if (!this.isActive || dy === 0) return;
    this.zoomTo(this.zoomStep + (dy > 0 ? 1 : -1), pointer);
  };

  /** Zoom out or back in, keeping the point under the pointer (or the view's centre) in place. */
  private zoomTo(step: number, pointer?: Phaser.Input.Pointer): void {
    const next = Math.max(0, Math.min(ZOOMS.length - 1, step));
    const zoom = ZOOMS[next];
    if (zoom === undefined || next === this.zoomStep) return;
    const camera = this.options.scene.cameras.main;
    const screen = pointer ?? { x: camera.width / 2, y: camera.height / 2 };
    const before = camera.getWorldPoint(screen.x, screen.y);
    this.zoomStep = next;
    camera.setZoom(zoom);
    camera.preRender();
    const after = camera.getWorldPoint(screen.x, screen.y);
    camera.setScroll(
      Math.round(camera.scrollX + before.x - after.x),
      Math.round(camera.scrollY + before.y - after.y),
    );
    this.panel.say(zoom === 1 ? 'Zoom normal.' : `Zoom 1/${String(Math.round(1 / zoom))}.`);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'F2') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.toggle();
      return;
    }
    if (!this.isActive) return;
    const target = event.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) return;
    const key = event.key.toLowerCase();
    if (event.ctrlKey || event.metaKey) {
      // Only undo and save; the browser keeps every other shortcut.
      if (key !== 'z' && key !== 's') return;
      if (key === 'z') this.undo();
      else void this.save();
    } else if (key === 'escape') this.escape();
    else if (key === 'delete' || key === 'backspace') this.remove();
    else if (key === 'f') this.flip();
    else if (key === 'r') this.turn();
    else if (key.startsWith('arrow')) this.panBy(key);
    else if (key === '-' || key === '_') this.zoomTo(this.zoomStep + 1);
    else if (key === '+' || key === '=') this.zoomTo(this.zoomStep - 1);
    else if (event.altKey || key.length > 1) return;
    // Handled, or a letter the game would take (P, M, T, W…): it waits for the editor to close.
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.model.dirty) event.preventDefault();
  };

  private escape(): void {
    if (this.armed !== undefined) this.setArmed(undefined);
    else if (this.selection !== undefined) this.select(undefined);
    else this.toggle(false);
    this.drawOutline();
  }

  private undo(): void {
    if (!this.model.undo()) {
      this.panel.say('Rien à annuler.');
      return;
    }
    this.select(undefined);
    this.refresh();
  }

  private remove(): void {
    const selection = this.selection;
    if (selection === undefined) return;
    if (selection.kind === 'portal' || selection.kind === 'chest') {
      this.panel.say('Un portail ou un coffre se déplace, il ne se retire pas.', 'refused');
      return;
    }
    this.model.checkpoint();
    if (selection.kind === 'prop') this.model.removeProp(selection.origin);
    else this.model.removeSpawn(selection.origin);
    this.select(undefined);
    this.refresh();
  }

  private flip(): void {
    if (this.selection?.kind !== 'prop') return;
    const { origin } = this.selection;
    const placed = propOf(this.model.world, origin);
    if (placed === undefined) return;
    this.model.checkpoint();
    this.model.placeProp(origin, { x: placed.x, y: placed.y, flip: !placed.flip });
    this.refresh();
  }

  private turn(): void {
    if (this.selection?.kind !== 'spawn') return;
    const { origin } = this.selection;
    const den = spawnOf(this.model.world, origin);
    if (den === undefined) return;
    const next = FACINGS[(FACINGS.indexOf(den.facing ?? 'south') + 1) % FACINGS.length];
    this.model.checkpoint();
    this.model.placeSpawn(origin, { x: den.x, y: den.y, ...(next ? { facing: next } : {}) });
    this.refresh();
  }

  private panBy(key: string): void {
    const camera = this.options.scene.cameras.main;
    const step = PAN_TILES * TILE_SIZE;
    const dx = key === 'arrowleft' ? -step : key === 'arrowright' ? step : 0;
    const dy = key === 'arrowup' ? -step : key === 'arrowdown' ? step : 0;
    camera.setScroll(Math.round(camera.scrollX + dx), Math.round(camera.scrollY + dy));
  }

  private setArmed(entry: PaletteEntry | undefined): void {
    this.armed = entry;
    this.palette.setArmed(entry);
    if (entry !== undefined) {
      const name = entry.kind === 'prop' ? entry.id : MOBS[entry.id].name;
      this.panel.say(`Clique sur la carte pour poser : ${name}. Échap pour arrêter.`);
    }
  }

  /**
   * Set the armed entry down on a tile. An inhabitant starts alone and stays
   * put if it answers a click (merchant, smith), strolls if it is a
   * townsperson, and comes as a pack of three if it is a monster.
   */
  private setDown(entry: PaletteEntry, tile: { x: number; y: number }): void {
    this.model.checkpoint();
    if (entry.kind === 'prop') {
      this.model.addProp({ prop: entry.id, x: tile.x, y: tile.y, flip: false });
      this.select({ kind: 'prop', origin: `added:${String(this.model.current.added.length - 1)}` });
    } else {
      const mob = MOBS[entry.id];
      const hostile = !mob.peaceful;
      this.model.addSpawn({
        mob: entry.id,
        ...tile,
        count: hostile ? 3 : 1,
        radius: hostile ? 3 : mob.interactive ? 0 : 2,
        facing: 'south',
      });
      const index = this.model.current.addedSpawns.length - 1;
      this.select({ kind: 'spawn', origin: `added-spawn:${String(index)}` });
    }
    this.refresh();
  }

  private resize(size: { count: number; radius: number }): void {
    if (this.selection?.kind !== 'spawn') return;
    const den = spawnOf(this.model.world, this.selection.origin);
    if (den === undefined) return;
    const count = clamp(size.count, 1, 20, den.count);
    const radius = clamp(size.radius, 0, 8, den.radius);
    if (count === den.count && radius === den.radius) return;
    this.model.checkpoint();
    this.model.placeSpawn(this.selection.origin, { x: den.x, y: den.y, count, radius });
    this.refresh();
  }

  private async save(): Promise<void> {
    if (this.saving || !this.options.canSave) return;
    if (this.model.problems.length > 0) {
      this.panel.say('Corrige d’abord les problèmes listés.', 'refused');
      return;
    }
    this.saving = true;
    this.panel.say('Enregistrement…');
    const result = await saveWorldEdits(this.options.token, this.model.current);
    if (result.saved) this.model.markSaved();
    this.panel.say(result.message, result.saved ? 'done' : 'refused');
    this.saving = false;
    this.refresh();
  }

  private select(selection: Selection | undefined): void {
    this.selection = selection;
    const world = this.model.world;
    this.panel.setSelected(selection === undefined ? undefined : describe(world, selection));
    const den = selection?.kind === 'spawn' ? spawnOf(world, selection.origin) : undefined;
    this.panel.setSpawnSize(
      den === undefined ? undefined : { count: den.count, radius: den.radius },
    );
  }

  private moveTo(selection: Selection, to: { x: number; y: number }): void {
    const world = this.model.world;
    switch (selection.kind) {
      case 'prop': {
        const placed = propOf(world, selection.origin);
        if (placed !== undefined)
          this.model.placeProp(selection.origin, { ...to, flip: placed.flip });
        break;
      }
      case 'spawn':
        this.model.placeSpawn(selection.origin, to);
        break;
      case 'portal':
        this.model.placePortal(selection.id, to);
        break;
      case 'chest':
        this.model.placeChest(selection.id, to);
        break;
    }
  }

  /** Redraw the world from the model: props, chests, markers, outline, panel. */
  private refresh(): void {
    const world = this.model.world;
    const { scene, manifest } = this.options;
    const seen = new Set<string>();
    for (const { placement, origin } of world.props) {
      seen.add(origin);
      const shown = this.images.get(origin);
      if (shown?.placed.prop === placement.prop) {
        const { x, y, flip } = shown.placed;
        if (x !== placement.x || y !== placement.y || flip !== placement.flip) {
          placeProp(shown.image, placement);
          shown.placed = placement;
        }
        continue;
      }
      shown?.image.destroy();
      const image = drawProp(scene, manifest, placement);
      if (image === undefined) this.images.delete(origin);
      else this.images.set(origin, { image, placed: placement });
    }
    for (const [origin, shown] of this.images) {
      if (seen.has(origin)) continue;
      shown.image.destroy();
      this.images.delete(origin);
    }
    for (const chest of world.layout.chests) this.options.chests?.move(chest.id, chest);

    this.drawGhosts();
    const selected = this.selection;
    this.overlay.drawSpawns(world, selected?.kind === 'spawn' ? selected.origin : undefined);
    this.drawOutline();
    if (selected !== undefined) this.select(selected);
    this.panel.setState(this.model.count, this.model.dirty, this.model.problems);
  }

  /** The inhabitants set down by hand, standing on their tile, until the server spawns them. */
  private drawGhosts(): void {
    const { scene, manifest } = this.options;
    const seen = new Set<string>();
    for (const { den, origin } of this.model.world.spawns) {
      if (!origin.startsWith('added-spawn:') || this.servedOrigins.has(origin)) continue;
      const sheet = manifest.sheets[MOBS[den.mob].bodyKey];
      if (sheet === undefined) continue;
      const frame = `${sheet.prefix}/body/idle/south/0`;
      const key = `${origin}|${den.mob}`;
      seen.add(key);
      const image =
        this.ghosts.get(key) ??
        scene.add.image(0, 0, sheet.atlas, frame).setOrigin(0, 0).setAlpha(0.75);
      this.ghosts.set(key, image);
      const base = (den.y + 1) * TILE_SIZE;
      image
        .setPosition(Math.round((den.x + 0.5) * TILE_SIZE - image.width / 2), base - image.height)
        .setDepth(base)
        .setVisible(this.isActive);
    }
    for (const [key, image] of this.ghosts) {
      if (seen.has(key)) continue;
      image.destroy();
      this.ghosts.delete(key);
    }
  }

  private drawOutline(): void {
    const world = this.model.world;
    const hover =
      this.hover === undefined || sameSelection(this.hover, this.selection)
        ? undefined
        : boxOf(world, this.hover);
    const selected = this.selection === undefined ? undefined : boxOf(world, this.selection);
    this.overlay.drawOutline(hover, selected, this.model.problems.length > 0);
  }

  /**
   * What is under the pointer: an inhabitant's tile first, then a chest,
   * then the nearest prop whose footprint holds the tile or whose sprite
   * holds the point (a house is picked by its roof too).
   */
  private pick(pointer: Phaser.Input.Pointer): Selection | undefined {
    const tile = this.tileOf(pointer);
    const point = this.pointOf(pointer);
    const world = this.model.world;
    const live = this.options.mobAt(tile) ?? this.options.mobAt({ x: tile.x, y: tile.y + 1 });
    if (live !== undefined) {
      const home = this.homeOf(live);
      if (home !== undefined) return { kind: 'spawn', origin: home };
    }
    const spawn = world.spawns.find(({ den }) => den.x === tile.x && den.y === tile.y);
    if (spawn !== undefined) return { kind: 'spawn', origin: spawn.origin };
    const chest = this.options.chests?.chestAt(tile);
    if (chest !== undefined) return { kind: 'chest', id: chest.id };

    let best: { origin: string; depth: number; placed: PropPlacement } | undefined;
    for (const [origin, { image, placed }] of this.images) {
      if (FIXED_PROP_IDS.has(placed.prop) && placed.prop !== 'portal') continue;
      const box = footprintOf(placed);
      const onFootprint =
        tile.x >= box.x0 && tile.x <= box.x1 && tile.y >= box.y0 && tile.y <= box.y1;
      const onSprite =
        PROPS[placed.prop]?.blocks === true && image.getBounds().contains(point.x, point.y);
      if (!onFootprint && !onSprite) continue;
      // The footprint beats a sprite overhanging it; among equals, the nearest.
      const depth = image.depth + (onFootprint ? 10_000 : 0);
      if (best === undefined || depth >= best.depth) best = { origin, depth, placed };
    }
    if (best === undefined) return undefined;
    const arch = best.placed;
    if (arch.prop !== 'portal') return { kind: 'prop', origin: best.origin };
    const portal = world.layout.portals.find((p) => p.x === arch.x && p.y === arch.y);
    return portal === undefined ? undefined : { kind: 'portal', id: portal.id };
  }

  /**
   * The spawn a live inhabitant came from: the server does not say, so the
   * nearest one of its kind as the server loaded them. They wander within
   * their spawn's radius, and two spawns of a kind keep their distance.
   */
  private homeOf(live: { mob: string; x: number; y: number }): string | undefined {
    let best: { origin: string; distance: number } | undefined;
    for (const { den, origin } of this.served) {
      if (den.mob !== live.mob) continue;
      const distance = Math.abs(den.x - live.x) + Math.abs(den.y - live.y);
      if (best === undefined || distance < best.distance) best = { origin, distance };
    }
    return best?.origin;
  }

  private pointOf(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    const point = this.options.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
    return { x: point.x, y: point.y };
  }

  private tileOf(pointer: Phaser.Input.Pointer): { x: number; y: number } {
    const point = this.pointOf(pointer);
    return { x: Math.floor(point.x / TILE_SIZE), y: Math.floor(point.y / TILE_SIZE) };
  }
}

/** A typed number, whole and within bounds; the current value when it is not a number. */
function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}
